import Vue from 'vue';
import VueRouter from 'vue-router';
import store from '@/store';
import config from '@/config';
import interfaces from './interfaces';

Vue.use(VueRouter);

const checkAvailable = ((route) => {
  const { meta } = route;

  return (!meta.getAvailability || meta.getAvailability(store.getters));
});

const redirectIfNotAuthorized = (to, from, next, p) => {
  const { $log } = Vue.prototype;
  $log.debug('Check Auth', `${p} == "${from.path}" -> "${to.path}"`);

  if (!store.getters.user) {
    $log.debug('Redirect - Empty user');
    const { name, path, query } = to;
    store.commit('setAuthRedirectLocation', { name, path, query });
    next({ name: 'SignIn', replace: true });
    return;
  }

  const { authLocation } = store.getters;
  if (authLocation) {
    store.commit('clearAuthRedirectLocation');
    // eslint-disable-next-line no-use-before-define
    const isAvailable = router.resolve(authLocation).route.matched
      .reduce((res, route) => res && checkAvailable(route), true);
    if (isAvailable) {
      $log.debug('Redirect - authLocation');
      next({ ...authLocation, replace: true });
    } else {
      $log.debug('Redirect - authLocation - Forbidden');
      next({ name: 'Forbidden' });
    }
    return;
  }

  $log.debug('Redirect - Next');
  next();
};

const routes = [
  {
    path: '/',
    exact: true,
    component: () => import('@/components/app/AppLayout.vue'),
    beforeEnter: (to, from, next) => redirectIfNotAuthorized(to, from, next, '/'),
    children: [
      {
        path: '',
        redirect: { name: 'BulletinBoard', replace: true },
      },
      {
        path: 'users',
        name: 'UserList',
        component: () => import('@/views/UserList.vue'),
      },
      {
        path: 'service/logs',
        name: 'LogList',
        component: () => import('@/views/LogList.vue'),
      },
      {
        path: 'service/queues',
        name: 'QueuesList',
        component: () => import('@/views/QueuesList.vue'),
      },
      {
        path: 'suppliers/list',
        name: 'SupplierList',
        component: () => import('@/views/SupplierList.vue'),
      },
      {
        path: 'suppliers/contacts',
        name: 'SupplierContacts',
        props: true,
        component: () => import('@/views/SupplierContacts.vue'),
      },
      {
        path: 'suppliers/supplier/:asdb?',
        name: 'SupplierItem',
        component: () => import('@/views/SupplierItem.vue'),
        props: true,
      },
      ...config.useAutoOrders ? [
        {
          path: 'orders/order-groups',
          name: 'OrderGroupList',
          component: () => import('@/views/orders/OrderGroupList.vue'),
        },
        {
          path: 'orders/order-groups/:id',
          name: 'OrderGroup',
          props: true,
          component: () => import('@/views/orders/OrderGroup.vue'),
        },
        {
          path: 'orders/nearest-shipment',
          name: 'NearestShipment',
          component: () => import('@/views/orders/NearestShipment.vue'),
          props: true,
        },
        {
          path: 'orders/nearest-shipment-by-supplier',
          name: 'NearestShipmentBySupplier',
          component: () => import('@/views/orders/NearestShipmentBySupplier.vue'),
        },
        {
          path: 'orders/reports-by-part',
          name: 'ReportListByPart',
          component: () => import('@/views/orders/ReportListByPart.vue'),
        },
        {
          path: 'orders/reports-by-parts',
          name: 'ReportListByParts',
          component: () => import('@/views/orders/ReportListByParts.vue'),
        },
        {
          path: 'orders/history-by-days',
          name: 'HistoryByDays',
          component: () => import('@/views/orders/HistoryByDays.vue'),
        },
        {
          path: 'orders/history-by-weeks',
          name: 'HistoryByWeeks',
          component: () => import('@/views/orders/HistoryByWeeks.vue'),
        },
        {
          path: 'orders/promises',
          name: 'PromiseList',
          component: () => import('@/views/orders/PromiseList.vue'),
        }] : [],

      ...config.useManualOrders ? [
        {
          path: 'manual-orders',
          name: 'ManualOrdersList',
          component: () => import('@/views/manual-orders/ManualOrderList.vue'),
        },
        {
          path: 'manual-orders/:id',
          name: 'ManualOrder',
          component: () => import('@/views/manual-orders/ManualOrder.vue'),
          props: true,
        },
        {
          path: 'manual-orders/:id/status-history',
          name: 'ManualOrderStatusHistory',
          component: () => import('@/views/manual-orders/ManualOrderStatusHistory.vue'),
          props: true,
        },
        {
          path: 'manual-orders/:id/shipment-status-history',
          name: 'ManualOrderShipmentStatusHistory',
          component: () => import('@/views/manual-orders/ManualOrderShipmentStatusHistory.vue'),
          props: true,
        },
        {
          path: 'manual-orders-report',
          name: 'ManualOrdersReport',
          component: () => import('@/views/manual-orders/ManualOrdersReport.vue'),
        },
        {
          path: 'manual-order-fulfillment-report',
          name: 'ManualOrderFillmentReport',
          component: () => import('@/views/manual-orders/ManualOrderFulfillmentReport.vue'),
        },
      ] : [],
      {
        path: 'reports/parts-properties',
        name: 'PartsProperties',
        component: () => import('@/views/reports/parts-properties.vue'),
      },
      {
        path: 'reports/parts-prices',
        name: 'PartsPrices',
        component: () => import('@/views/reports/parts-prices.vue'),
      },
      {
        path: 'reports/parts-new',
        name: 'PartsNew',
        component: () => import('@/views/reports/parts-new.vue'),
      },
      {
        path: 'reports/parts-missing',
        name: 'PartsMissing',
        component: () => import('@/views/reports/parts-missing.vue'),
      },
      {
        path: 'bulletins/board',
        name: 'BulletinBoard',
        component: () => import('@/views/bulletins/BulletinBoard.vue'),
      },
      {
        path: 'bulletins/board/:id',
        name: 'BulletinDetails',
        props: true,
        component: () => import('@/views/bulletins/BulletinDetails.vue'),
      },
      {
        path: 'bulletins/list',
        name: 'BulletinList',
        component: () => import('@/views/bulletins/BulletinList.vue'),
      },
      {
        path: 'bulletins/list/create',
        name: 'BulletinCreation',
        component: () => import('@/views/bulletins/BulletinCreation.vue'),
      },
      {
        path: 'emails/list',
        name: 'EmailList',
        component: () => import('@/views/emails/EmailList.vue'),
      },
      {
        path: 'emails/list/:id',
        name: 'Email',
        props: true,
        component: () => import('@/views/emails/Email.vue'),
      },
      {
        path: 'emails/templates',
        name: 'TemplateList',
        component: () => import('@/views/emails/TemplateList.vue'),
      },
      {
        path: 'emails/templates/:name',
        name: 'EmailTemplate',
        props: true,
        component: () => import('@/views/emails/EmailTemplate.vue'),
      },
      {
        path: 'roles',
        name: 'RoleList',
        component: () => import('@/views/RoleList.vue'),
      },
      {
        path: 'aftersales-orders',
        name: 'AftersalesOrders',
        component: () => import('@/views/aftersales/AftersalesOrderList.vue'),
      },
      {
        path: 'aftersales-orders/sales-report',
        name: 'AftersalesSalesReport',
        component: () => import('@/views/aftersales/AftersalesSalesReport.vue'),
      },
      {
        path: 'aftersales-orders/:number',
        name: 'AftersalesOrder',
        props: true,
        component: () => import('@/views/aftersales/AftersalesOrder.vue'),
      },
      {
        path: 'asn',
        name: 'ShipmentNoticeList',
        component: () => import('@/views/asn/ShipmentNoticeList.vue'),
      },
      {
        path: 'asn/create',
        name: 'ShipmentNoticeCreation',
        props: true,
        component: () => import('@/views/asn/ShipmentNoticeCreation.vue'),
      },
      {
        path: 'asn/:id',
        name: 'ShipmentNoticeDetails',
        props: true,
        component: () => import('@/views/asn/ShipmentNoticeDetails.vue'),
      },
      {
        path: 'asn/:id/edit',
        name: 'ShipmentNoticeEdit',
        props: true,
        component: () => import('@/views/asn/ShipmentNoticeEdit.vue'),
      },
      {
        path: 'asn/:id/track-statuses',
        name: 'TrackStatusHistory',
        props: true,
        component: () => import('@/views/asn/TrackStatusHistory.vue'),
      },
      {
        path: 'asn/:id/customs-statuses',
        name: 'CustomsStatusHistory',
        props: true,
        component: () => import('@/views/asn/CustomsStatusHistory.vue'),
      },
      {
        path: 'asn/:id/system-statuses',
        name: 'SystemStatusHistory',
        props: true,
        component: () => import('@/views/asn/SystemStatusHistory.vue'),
      },
      {
        path: 'labels/print',
        name: 'LabelPrinting',
        component: () => import('@/views/labels/LabelPrinting.vue'),
      },
      {
        path: 'labels/templates',
        name: 'LabelTemplates',
        component: () => import('@/views/labels/LabelTemplates.vue'),
      },
      {
        path: 'files',
        name: 'FileList',
        component: () => import('@/views/files/FileList.vue'),
      },
      {
        path: 'service/versions',
        name: 'VersionList',
        component: () => import('@/views/versions/VersionList.vue'),
      },
      {
        path: 'consolidator/asn-list',
        name: 'ConsolidatorAsnList',
        component: () => import('@/views/consolidator-asn/ConsolidatorAsnList.vue'),
      },
      {
        path: 'consolidator/asn-receiving',
        name: 'ConsolidatorAsnReceiving',
        component: () => import('@/views/consolidator-asn/ConsolidatorAsnReceiving.vue'),
      },
      {
        path: 'consolidator/asn-receiving-list',
        name: 'AsnReceivingList',
        component: () => import('@/views/consolidator-asn/AsnReceivingList.vue'),
      },
      {
        path: 'consolidator/asn-receiving/:id',
        name: 'ConsolidatorReceivingDetails',
        component: () => import('@/views/consolidator-asn/ReceivingDetails.vue'),
        props: true,
      },
      {
        path: 'consolidator/asn-shipment',
        name: 'ConsolidatorAsnShipment',
        component: () => import('@/views/consolidator-asn/ConsolidatorAsnShipment.vue'),
      },
      {
        path: 'consolidator/asn-shipment/:id',
        name: 'ConsolidatorShipmentEdit',
        component: () => import('@/views/consolidator-asn/ConsolidatorShipmentEdit.vue'),
        props: true,
      },
      {
        path: 'forbidden',
        name: 'Forbidden',
        component: () => import('@/views/Forbidden.vue'),
      },
    ],
  },
  {
    path: '/sign-in',
    name: 'SignIn',
    component: () => import('@/views/SignIn.vue'),
  },
  {
    path: '/sign-in/not-registred',
    name: 'UserNotRegistred',
    component: () => import('@/views/UserNotRegistred.vue'),
  },
  {
    path: '/reset-password',
    name: 'ResetPassword',
    component: () => import('@/views/ResetPassword.vue'),
  },
  {
    path: '/main/token-validate',
    name: 'EmailVerification',
    component: () => import('@/views/EmailVerification.vue'),
    props: (route) => ({ verificationCode: route.query.token }),
  },
  {
    path: '*',
    name: 'NotFound',
    component: () => import('@/views/NotFound.vue'),
  },
];

routes[0].children.forEach((route) => {
  route.meta = { getAvailability: interfaces[route.path] };
  route.beforeEnter = (to, from, next) => redirectIfNotAuthorized(to, from, next, route.path);
});

const router = new VueRouter({
  mode: 'history',
  routes,
  scrollBehavior() {
    return {
      x: 0,
      y: 0,
    };
  },
});

router.onError((err) => {
  // App was updated. Need to be reloaded
  if (err.name === 'ChunkLoadError') {
    router.go(0);
    return;
  }

  // All other errors should be logged
  throw err;
});

export default router;
